import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import { setTablePreference } from '@repo/util/tablePreferenceStorage';
import { getZodDefaults } from '@repo/util/getZodDefaults';
import type { ZodObjecty } from '@repo/types/Types';
import type { UseZodQueryReturn } from './useZodQuery';

const DEFAULT_OMIT = ['page'];

export type UseFilterPreferenceParams<Schema extends ZodObjecty> = {
  /** Stable identifier for the table these preferences belong to (e.g. 'accounts'). */
  tableKey: string;
  /** The current user's id; preferences are stored per user. */
  userId: string;
  /** The same Zod schema passed to `useZodQuery` (used to compare against defaults). */
  schema: Schema;
  /** The `useZodQuery` filter whose values should be persisted. */
  filter: UseZodQueryReturn<Schema>;
  /** Query keys that should never be persisted (defaults to pagination). */
  omitKeys?: string[];
};

/**
 * Persists a `useZodQuery` filter (sort + filters) to localStorage, keyed per user. Only
 * meaningfully non-default values are stored, so a user who never customizes gets no saved
 * pref. Restoring the saved values on return is handled separately by
 * `useFilterRestoreRedirect` (which must run above the table — restoring after the table
 * mounts races its Pagination and gets stripped).
 */
export function useFilterPreference<Schema extends ZodObjecty>({
  tableKey,
  userId,
  schema,
  filter,
  omitKeys = DEFAULT_OMIT,
}: UseFilterPreferenceParams<Schema>): void {
  // Stringified schema defaults, so we only persist values the user actually changed.
  const defaultsStr = useMemo(() => {
    const defaults = getZodDefaults(schema) as Record<string, unknown>;
    const out: Record<string, string> = {};
    for (const [key, value] of Object.entries(defaults)) {
      out[key] = value == null ? '' : String(value);
    }
    return out;
  }, [schema]);

  // Serialize the meaningful (non-default, non-empty) values to a stable string so the
  // effect only fires on real changes.
  const serialized = useMemo(() => {
    const values: Record<string, string> = {};
    for (const [key, value] of Object.entries(filter.queryValues as Record<string, unknown>)) {
      if (omitKeys.includes(key)) continue;
      const str = value == null ? '' : String(value);
      if (str === '') continue;
      if (defaultsStr[key] === str) continue;
      values[key] = str;
    }
    return JSON.stringify(values);
  }, [filter.queryValues, omitKeys, defaultsStr]);

  // The path this table mounted on, so we can tell an in-page Clear apart from a route
  // transition (both make `queryValues` collapse to defaults).
  const location = useLocation();
  const [mountPath] = useState(() => location.pathname);

  useEffect(() => {
    if (!userId) return;
    const values = JSON.parse(serialized) as Record<string, string>;
    // An empty result means the user cleared the filters (or reset everything to defaults) —
    // persist that so it erases the saved pref. But `queryValues` ALSO momentarily collapses
    // to empty during a route transition (navigating away re-renders this component with
    // empty search params before it unmounts); that must NOT wipe the pref. So only allow an
    // empty write while we're still on the table's own route.
    if (Object.keys(values).length === 0 && location.pathname !== mountPath) return;
    setTablePreference(userId, tableKey, values);
  }, [serialized, userId, tableKey, location.pathname, mountPath]);
}
